import React, { useEffect, useRef, useState } from "react";
import RadiantLabel from "./RadiantLabel";
import LegacyLabel from "./LegacyLabel";

const CASE_WIDTH = 355;
const CASE_HEIGHT = 600;

const SIZE_SCALES = {
  xxs: 0.2,
  xs: 0.3,
  xsm: 0.4,
  sm: 0.5,
  md: 0.7,
  lg: 1,
  xl: 1.1,
  xxl: 1.3,
  xxxl: 1.4,
};

// Contenitore del gemello digitale. Le carte singole stanno nel case rigido, a meno che non siano state
// gradate su sleeve. Bustine e sigillati invece stanno sempre in una sleeve morbida, anche se registrati
// su case: non c'e' un case digitale per loro, e quello della carta li deformerebbe. La forma della
// sleeve dipende dalla categoria: stretta per le bustine, larga per gli altri sigillati
const containerFor = (encasing, categoryName) => {
  const category = (categoryName || 'singles').toLowerCase();

  if (category === 'singles') return encasing === 'sleeve' ? 'sleeve-card' : 'case';
  if (category === 'booster pack') return 'sleeve-booster-pack';

  return 'sleeve-sealed';
};

// Nella sleeve il prodotto deve essere largo quanto l'etichetta, come la carta nel case. Misure nel riquadro
// base 355x600: l'etichetta e' larga 253px, centrata, e finisce a 159px dal bordo alto. Il prodotto parte poco
// sotto, con la crimpatura delle bustine tutta in vista; quello che sfora in basso si taglia sul bordo interno
// della sleeve
const SLEEVE_LABEL_WIDTH = 253;
const SLEEVE_PRODUCT_TOP = 175;

// Nella sleeve delle bustine etichetta e prodotto stanno un po' piu' in alto, piu' vicini all'intaglio: deve
// valere quanto lo spostamento dell'etichetta in digital_case_overlay.scss ($dco-booster-pack-lift)
const SLEEVE_LIFT = { 'sleeve-booster-pack': 34.5 };
const SLEEVE_INNER_BOTTOM = 592;

// Le foto del catalogo sono portate tutte al formato carta, e i prodotti piu' stretti (le bustine) ci stanno
// dentro con delle bande trasparenti ai lati, diverse da foto a foto. Per allineare il prodotto all'etichetta
// serve la parte davvero visibile, che si ricava dai pixel: basta una copia piccola dell'immagine
const opaqueBounds = (img) => {
  const sampleWidth = Math.min(img.naturalWidth, 200);
  const sampleHeight = Math.round(img.naturalHeight * sampleWidth / img.naturalWidth);
  const canvas = document.createElement('canvas');
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;
  const context = canvas.getContext('2d');
  context.drawImage(img, 0, 0, sampleWidth, sampleHeight);
  const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);

  // Bordi pieni di ogni riga: le ombre e i contorni sfumati attorno al prodotto non contano
  const rows = [];
  for (let y = 0; y < sampleHeight; y++) {
    let left = -1, right = -1;
    for (let x = 0; x < sampleWidth; x++) {
      if (data[(y * sampleWidth + x) * 4 + 3] <= 128) continue;
      if (left < 0) left = x;
      right = x;
    }
    if (left >= 0) rows.push({ y, left, right: right + 1 });
  }
  if (rows.length === 0) return null;

  // I lati si prendono dal corpo del prodotto, cioe' dalle righe centrali, e non dalla riga piu' larga: la
  // crimpatura delle bustine sporge oltre il corpo e finisce comunque sotto l'etichetta
  const body = rows.slice(Math.floor(rows.length * 0.15), Math.ceil(rows.length * 0.85));
  const median = (values) => values.sort((a, b) => a - b)[Math.floor(values.length / 2)];

  return {
    left: median(body.map(row => row.left)) / sampleWidth,
    right: median(body.map(row => row.right)) / sampleWidth,
    top: rows[0].y / sampleHeight,
    bottom: (rows[rows.length - 1].y + 1) / sampleHeight
  };
};

// Posizione dello slot, nel riquadro base, per cui la parte visibile del prodotto e' larga quanto l'etichetta e
// parte poco sotto di essa. Un prodotto troppo alto per la sleeve non si rimpicciolisce: si taglia sotto
const sleeveSlotStyle = (img, bounds, container) => {
  const width = SLEEVE_LABEL_WIDTH / (bounds.right - bounds.left);
  const height = width * img.naturalHeight / img.naturalWidth;
  const top = SLEEVE_PRODUCT_TOP - (SLEEVE_LIFT[container] || 0) - bounds.top * height;
  const overflow = Math.max(0, top + height - SLEEVE_INNER_BOTTOM);
  const opaqueCenter = (bounds.left + bounds.right) / 2;

  return {
    left: `${CASE_WIDTH / 2 - opaqueCenter * width}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    transform: 'none',
    clipPath: `inset(0 0 ${overflow}px 0)`
  };
};

const DigitalCaseOverlay = ({
  labelLines = [],
  titleLine,
  serialNumber,
  grading,
  evaluation,
  size = 'lg',
  categoryName,
  children,
}) => {
  const container = grading ? containerFor(grading.encasing, categoryName || grading.category_name) : null;
  const isSleeve = container !== null && container !== 'case';

  const slotRef = useRef(null);
  // Misura del prodotto per l'immagine che c'e' ora nello slot: cambiata l'immagine, va rifatta
  const [productFit, setProductFit] = useState(null);

  useEffect(() => {
    if (!isSleeve) return;

    const img = slotRef.current?.querySelector('img');
    if (!img) return;

    const src = img.currentSrc || img.src;
    if (productFit?.src === src) return;

    const measure = () => {
      let style = null;
      try {
        const bounds = opaqueBounds(img);
        if (bounds) style = sleeveSlotStyle(img, bounds, container);
      } catch (error) {
        // Immagine non leggibile (es. da un altro dominio): resta lo slot standard della sleeve
      }
      setProductFit({ src, style });
    };

    if (img.complete && img.naturalWidth > 0) {
      measure();
      return;
    }

    img.addEventListener('load', measure, { once: true });
    return () => img.removeEventListener('load', measure);
  });

  if (!grading) return children || null;

  const scale = SIZE_SCALES[size] || 1;
  const LabelComponent = grading.label_type === 'legacy' ? LegacyLabel : RadiantLabel;
  const currentSrc = slotRef.current?.querySelector('img')?.currentSrc;
  const slotStyle = isSleeve && productFit?.style && (!currentSrc || productFit.src === currentSrc) ? productFit.style : undefined;

  return (
    <div
      className={`dco${isSleeve ? ` dco--sleeve dco--${container}` : ''}`}
      style={{ width: `${CASE_WIDTH * scale}px`, height: `${CASE_HEIGHT * scale}px` }}
    >
      <div
        className="dco__inner"
        style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
      >
        {isSleeve ? <div className="dco__sleeve" /> : <div className="dco__background" />}

        <LabelComponent
          labelLines={labelLines}
          titleLine={titleLine}
          serialNumber={serialNumber}
          grading={grading}
          evaluation={evaluation}
        />

        <div className="dco__card-slot" ref={slotRef} style={slotStyle}>
          {children}
        </div>

        {/* I riflessi della plastica passano sopra al prodotto, che sta dentro la sleeve */}
        {isSleeve && <div className="dco__sleeve-gloss" />}
      </div>
    </div>
  );
};

export default DigitalCaseOverlay;
